import { formatDate } from '../Business/utility/DateFormatter';
import { Environment } from '../config/environment';
import { Backend } from './Backend';
import {
  ICategory,
  IChannelRightsV5,
  IChannelV5,
  IProgram,
  ISchedule,
  IScheduleItem,
  ISeries,
} from './models/AndromeTV.types';

export class EpgApi {
  /**
   * Request all channels
   */
  static channels(backend: Backend): Promise<IChannelV5[]> {
    const url = backend.makeUrl('epg/v5/channel');
    return backend.getJson(url, backend.bearer) as Promise<IChannelV5[]>;
  }

  /**
   * Get the schedules for a channel
   */
  static channelSchedules(backend: Backend, channelId: string, from: Date, until: Date): Promise<ISchedule> {
    const fromFormatted = formatDate(from);
    const untilFormatted = formatDate(until);
    const url = backend.makeUrl(
      `epg/v1/schedule/channel/${encodeURIComponent(channelId)}/from/${encodeURIComponent(
        fromFormatted
      )}/until/${encodeURIComponent(untilFormatted)}`
    );
    return backend.getJson(url, backend.bearer) as Promise<ISchedule>;
  }

  /**
   * Get program
   */
  static program(backend: Backend, id: string, from: Date, until: Date, catalogId?: string): Promise<IProgram> {
    const query: any = {};
    query.from = formatDate(from);
    query.until = formatDate(until);
    query.catalogId = catalogId;
    const url = backend.makeUrl(`epg/v2/program/${id}`, query);

    return backend.getJson(url, backend.bearer) as Promise<IProgram>;
  }

  static schedule(backend: Backend, scheduleId: string): Promise<IScheduleItem> {
    const url = backend.makeUrl(`epg/v1/schedule/${scheduleId}`);
    return backend.getJson(url, backend.bearer) as Promise<IScheduleItem>;
  }

  static channelRights(backend: Backend): Promise<IChannelRightsV5[]> {
    const query: any = {};
    query.type = Environment.system;
    query.entitlementId = backend.sessionInfo?.device.entitlementId;
    const url = backend.makeUrl('epg/v5/channel-right', query);

    return backend.getJson(url, backend.bearer) as Promise<IChannelRightsV5[]>;
  }

  static series(backend: Backend, id: string, from: Date, until: Date, catalogId: string): Promise<ISeries> {
    const query: any = {};
    query.from = formatDate(from);
    query.until = formatDate(until);
    query.catalogId = catalogId;
    const url = backend.makeUrl(`epg/v2/series/${id}`, query);

    return backend.getJson(url, backend.bearer) as Promise<ISeries>;
  }

  static scheduleForChannel(
    backend: Backend,
    channelId: string,
    from: Date,
    until: Date,
    tags: string[] = []
  ): Promise<ISchedule> {
    const queryParams: any = {};
    queryParams['tag'] = [...tags];

    const url = backend.makeUrl(
      `epg/v1/schedule/channel/${channelId}/from/${formatDate(from)}/until/${formatDate(until)}`,
      queryParams
    );

    return backend.getJson(url, backend.bearer) as Promise<ISchedule>;
  }

  static channelsWithTags(backend: Backend, categories: string[], from: Date, until: Date): Promise<string[]> {
    const queryParams: any = {};
    queryParams['tag'] = [];
    categories.forEach(tag => queryParams['tag'].push(tag));

    const catalogId = backend.deviceProfile?.profile.catalog;
    if (catalogId) {
      queryParams['catalogId'] = catalogId;
    }
    const url = backend.makeUrl(
      `epg/v1/category/from/${encodeURIComponent(formatDate(from))}/until/${encodeURIComponent(formatDate(until))}`,
      queryParams
    );

    return backend.getJson(url, backend.bearer) as Promise<string[]>;
  }

  static categories(backend: Backend): Promise<ICategory[]> {
    const url = backend.makeUrl('epg/v1/category');
    return backend.getJson(url, backend.bearer) as Promise<ICategory[]>;
  }
}
